import { getBitmapPixelColour } from "../bitmap/pixel.ts";
import type { BrickColour } from "../model/bricks.ts";
import { addPositions, subtractPositions } from "../model/geom.ts";
import type {
	BrickPosition,
	BuildBitmap,
	PictureConfiguration,
} from "../model/picture.ts";

function findFloodPositions(
	buildBitmap: BuildBitmap,
	{ imageZoomOffset }: Pick<PictureConfiguration, "imageZoomOffset">,
	position: BrickPosition,
	selectedColour: BrickColour,
) {
	const buildPosition = addPositions(position, imageZoomOffset);
	const pixelColour = getBitmapPixelColour(
		buildBitmap,
		buildPosition.x,
		buildPosition.y,
	);
	// No change, clicked colour is same as selected. Note that this still adds a command to the
	// stack and it shouldn't.
	if (pixelColour === selectedColour.rgba) {
		console.warn("Pixel is already selected");
		return;
	}

	const visited = new Set<string>();
	const matching = new Set<BrickPosition>([buildPosition]);

	const toPositionId = (p: BrickPosition): string => `${p.x}x${p.y}`;

	visited.add(toPositionId(buildPosition));
	const positionsToCheck = [buildPosition];
	// Was using recursion by ran into max call stack size issues
	while (positionsToCheck.length > 0) {
		const p = positionsToCheck.pop();
		if (!p) {
			break;
		}

		if (getBitmapPixelColour(buildBitmap, p.x, p.y) !== pixelColour) {
			/* eslint-disable-next-line no-continue */
			continue;
		}

		matching.add(p);

		const potentialPositions = [
			{ x: p.x - 1, y: p.y },
			{ x: p.x + 1, y: p.y },
			{ x: p.x, y: p.y - 1 },
			{ x: p.x, y: p.y + 1 },
		];
		const validPositions = potentialPositions.filter(
			(pP) =>
				pP.x >= 0 &&
				pP.y >= 0 &&
				pP.x < buildBitmap.width &&
				pP.y < buildBitmap.height,
		);
		const nonVisitedPositions = validPositions.filter(
			(nVP) => !visited.has(toPositionId(nVP)),
		);
		nonVisitedPositions.forEach((nVP) => {
			visited.add(toPositionId(nVP));
		});
		positionsToCheck.splice(0, 0, ...nonVisitedPositions);
	}

	return Array.from(matching, (p) => subtractPositions(p, imageZoomOffset));
}

export { findFloodPositions };
